import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AsyncReduxState, initialAsyncReduxState } from '../../../redux/@config/AsyncReduxState'
import { ValkTalkFeedType, ValkTalkPost } from '../../entities/ValkTalkPost/ValkTalkPost'
import { formatDate } from '../../helpers/date'
import { DateFormat } from '../../types/dateFormatTypes'
import { globalFiltersSlice } from '../@interface/globalFilters/globalFiltersReducer'

export type ValkTalkPostsState = AsyncReduxState<{
    feedTypes: ValkTalkFeedType[]
    onlyOwnPosts: boolean
    month: string
    search: string
    posts: ValkTalkPost[]
    hasMorePosts: boolean
    savedPosts: ValkTalkPost[]
    deletedPostIds: string[]
    isAddPostModalOpen: boolean
}>

const initialState: ValkTalkPostsState = {
    ...initialAsyncReduxState,
    isLoading: true,
    feedTypes: [],
    onlyOwnPosts: false,
    month: formatDate(new Date(), DateFormat.inputMonth),
    search: '',
    posts: [],
    hasMorePosts: false,
    savedPosts: [],
    deletedPostIds: [],
    isAddPostModalOpen: false,
}

export const valkTalkPostsSlice = createSlice({
    name: 'valkTalkPostsReducer',
    initialState,
    reducers: {
        setIsLoading(state, action: PayloadAction<boolean>): ValkTalkPostsState {
            return {
                ...state,
                isLoading: action.payload,
            }
        },
        setIsDeleting(state, action: PayloadAction<boolean>): ValkTalkPostsState {
            return {
                ...state,
                isDeleting: action.payload,
            }
        },
        setError(state, action: PayloadAction<string>): ValkTalkPostsState {
            return {
                ...state,
                error: action.payload,
            }
        },
        setPosts(state, action: PayloadAction<{ posts: ValkTalkPost[], hasMorePosts: boolean }>): ValkTalkPostsState {
            return {
                ...state,
                posts: action.payload.posts,
                hasMorePosts: action.payload.hasMorePosts,
            }
        },
        setSavedPost(state, action: PayloadAction<ValkTalkPost>): ValkTalkPostsState {
            return {
                ...state,
                savedPosts: [
                    ...state.savedPosts.filter(post => post.id !== action.payload.id),
                    action.payload,
                ],
            }
        },
        removePost(state, action: PayloadAction<string>): ValkTalkPostsState {
            return {
                ...state,
                posts: state.posts.filter(post => post.id !== action.payload),
                savedPosts: state.savedPosts.filter(post => post.id !== action.payload),
                deletedPostIds: [...state.deletedPostIds, action.payload],
            }
        },
        setIsAddPostModalOpen(state, action: PayloadAction<boolean>): ValkTalkPostsState {
            return {
                ...state,
                isAddPostModalOpen: action.payload,
            }
        },
        setFeedTypes(state, action: PayloadAction<ValkTalkFeedType[]>): ValkTalkPostsState {
            return {
                ...state,
                feedTypes: action.payload,
            }
        },
        setOnlyOwnPosts(state, action: PayloadAction<boolean>): ValkTalkPostsState {
            return {
                ...state,
                onlyOwnPosts: action.payload,
            }
        },
        setMonth(state, action: PayloadAction<string>): ValkTalkPostsState {
            return {
                ...state,
                month: action.payload,
            }
        },
        setSearch(state, action: PayloadAction<string>): ValkTalkPostsState {
            return {
                ...state,
                search: action.payload,
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(globalFiltersSlice.actions.resetFilters, state => ({
            ...state,
            feedTypes: [],
            onlyOwnPosts: false,
            search: '',
        }))
    },
})

export const {
    setIsLoading,
    setIsDeleting,
    setError,
    setPosts,
    setSavedPost,
    removePost,
    setIsAddPostModalOpen,
    setFeedTypes,
    setOnlyOwnPosts,
    setMonth,
    setSearch,
} = valkTalkPostsSlice.actions

export const valkTalkPostsReducer = valkTalkPostsSlice.reducer
